import React, { useState, useEffect, useRef, useCallback } from "react";
import { getOptimizedAvatarUrl } from '../../utils/cloudinary';

const CreatorSearchAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search by creator name, handle, or location...",
  style = {},
  inputStyle = {},
  labelStyle = {},
  label = "",
  autoFocus = false,
  disabled = false,
  fetchCreators,
  selectedCreator = null,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  const performSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 1 || disabled) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      const results = await fetchCreators(searchTerm);
      setSuggestions(results || []);
      setShowSuggestions(results && results.length > 0);
    } catch (error) {
      console.error('Error fetching creators:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  }, [fetchCreators, disabled]);

  // Debounce input changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, performSearch]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = (creator) => {
    onSelect(creator);
    onChange(formatCreatorDisplay(creator));
    setShowSuggestions(false);
  };

  const formatCreatorDisplay = (creator) => {
    if (!creator) return '';
    const firstName = creator.first_name || '';
    const lastInitial = creator.last_name ? creator.last_name[0] : '';
    const name = `${firstName}${lastInitial ? ` ${lastInitial}.` : ''}`.trim() || 'Creator';
    const handle = creator.handle ? `@${creator.handle}` : '';
    return handle ? `${handle} - ${name}` : name;
  };

  const formatCreatorName = (creator) => {
    if (!creator) return 'Creator';
    const firstName = creator.first_name || '';
    const lastInitial = creator.last_name ? creator.last_name[0] : '';
    return `${firstName}${lastInitial ? ` ${lastInitial}.` : ''}`.trim() || 'Creator';
  };

  const getAvatarUrl = (creator) => {
    const originalUrl = creator.avatar_asset_url || creator.avatar_url || null;
    if (!originalUrl) return null;
    
    // Transform to Cloudinary optimized URL
    return getOptimizedAvatarUrl(originalUrl);
  };

  const getInitials = (creator) => {
    const firstName = creator.first_name || '';
    const lastName = creator.last_name || '';
    if (firstName || lastName) {
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
    }
    return '?';
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const defaultInputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
    color: '#0f084b',
    outline: 'none',
    ...inputStyle
  };

  const defaultLabelStyle = {
    display: 'block',
    marginBottom: '8px',
    color: '#333',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
    fontSize: '14px',
    ...labelStyle
  };

  // Update display value when selectedCreator changes
  useEffect(() => {
    if (selectedCreator && !value) {
      onChange(formatCreatorDisplay(selectedCreator));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreator]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', ...style }}>
      {label && <label style={defaultLabelStyle}>{label}</label>}
      
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0 && !disabled) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        style={defaultInputStyle}
        autoFocus={autoFocus}
        disabled={disabled}
      />
      
      {loading && (
        <div style={{
          position: 'absolute',
          right: '12px',
          top: label ? '38px' : '10px',
          fontSize: '12px',
          color: '#999'
        }}>
          Searching...
        </div>
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid rgba(0,0,0,.12)',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxHeight: '300px',
          overflowY: 'auto',
          zIndex: 1000,
          marginTop: '4px'
        }}>
          {suggestions.map((creator, index) => {
            const avatarUrl = getAvatarUrl(creator);
            const name = formatCreatorName(creator);
            const handle = creator.handle ? `@${creator.handle}` : '';
            const location = creator.location_city && creator.location_state
              ? `${creator.location_city}, ${creator.location_state}`
              : creator.location_city || creator.location_state || '';
            
            return (
              <div
                key={creator.id}
                onClick={() => handleSuggestionClick(creator)}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  backgroundColor: index === highlightedIndex ? '#f0f0f0' : 'white',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '14px',
                  color: '#0f084b',
                  borderBottom: index < suggestions.length - 1 ? '1px solid rgba(0,0,0,.06)' : 'none',
                  transition: 'background-color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {/* Avatar Circle */}
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  overflow: 'hidden',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(0,0,0,.1)'
                }}>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={name}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const parent = e.target.parentElement;
                        e.target.style.display = 'none';
                        const initialsSpan = document.createElement('span');
                        initialsSpan.style.cssText = 'font-size: 12px; color: #666; font-weight: 500;';
                        initialsSpan.textContent = getInitials(creator);
                        parent.appendChild(initialsSpan);
                      }}
                    />
                  ) : (
                    <span style={{
                      fontSize: '12px',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {getInitials(creator)}
                    </span>
                  )}
                </div>
                
                {/* Creator Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ marginBottom: '2px' }}>
                    {handle ? (
                      <>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '14px',
                          color: '#0f084b'
                        }}>
                          {handle}
                        </span>
                        <span style={{ 
                          marginLeft: '6px', 
                          fontSize: '13px', 
                          color: '#666',
                          fontWeight: 'normal'
                        }}>
                          {name}
                        </span>
                      </>
                    ) : (
                      <span style={{ fontWeight: '500' }}>
                        {name}
                      </span>
                    )}
                  </div>
                  {location && (
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {location}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {showSuggestions && !loading && suggestions.length === 0 && value && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid rgba(0,0,0,.12)',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '12px',
          zIndex: 1000,
          marginTop: '4px',
          fontSize: '14px',
          color: '#666',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
        }}>
          No creators found
        </div>
      )}
    </div>
  );
};

export default CreatorSearchAutocomplete;

